import React from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import * as validators from '../../util/validators';

import css from './AuthenticationPage.module.css';

// Hidden input field
const FieldHidden = props => {
  const { name } = props;
  return (
    <Field id={name} name={name} type="hidden" className={css.unitTypeHidden}>
      {fieldRenderProps => <input {...fieldRenderProps?.input} />}
    </Field>
  );
};

/**
 * Return React Final Form Field that allows selecting user type.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class that overrides the default class css.userTypeSelect
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.name - The name of the field / input
 * @param {Array<propTypes.userType>} props.userTypes - The user types
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const FieldSelectUserType = props => {
  const { rootClassName, className, name, userTypes, intl } = props;
  const hasUserTypes = userTypes?.length > 0;
  const classes = classNames(rootClassName || css.userTypeSelect, className);

  return hasUserTypes ? (
    <Field
      name={name}
      validate={validators.required(intl.formatMessage({ id: 'FieldSelectUserType.required' }))}
    >
      {fieldRenderProps => {
        const { input, meta } = fieldRenderProps;
        const hasError = meta.touched && meta.invalid && meta.error;
        return (
          <div className={classes}>
            <label id={`${name}-label`} className={css.userTypeLabel}>
              {intl.formatMessage({ id: 'FieldSelectUserType.label' })}
            </label>
            <div
              className={css.userTypeOptions}
              role="radiogroup"
              aria-labelledby={`${name}-label`}
            >
              {userTypes.map(config => {
                const type = config.userType;
                const isSelected = input.value === type;
                return (
                  <button
                    key={type}
                    type="button"
                    className={classNames(css.userTypeOption, {
                      [css.userTypeOptionSelected]: isSelected,
                    })}
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => input.onChange(type)}
                    onBlur={input.onBlur}
                  >
                    {config.label}
                  </button>
                );
              })}
            </div>
            {hasError ? <div className={css.userTypeError}>{meta.error}</div> : null}
          </div>
        );
      }}
    </Field>
  ) : (
    <>
      <FieldHidden name={name} />
    </>
  );
};

export default FieldSelectUserType;
